import React from 'react';
import classNames from 'classnames';
import Image from 'common/image';
import withConfig from '@/config/withConfig';
import StarRatingWithCategory from 'common/star-rating-with-category';
import WalkingDistanceDisplay from 'common/spot-details/walking-distance';
import { Box, Flex, Heading, Text, Button } from '@spothero/ui';
import VisualFlag from '../visual-flag';
import { Price } from '../price/Price';
import { breakdownWODiscountGenerator } from 'utils/multiday-utils';
import { getSpotheroOversizeFee } from 'utils/search-vehicle';
import { usePrice } from 'hooks/spot/price/use-price';
import { useLabel } from 'hooks/spot/price/use-label';
import { useDispatch, useSelector } from 'react-redux';
import { setPriceBreakdownModalSpotId } from 'store/search/search-actions';
import { useShowTotal } from 'store/selectors/hooks/useShowTotal';
import { getParkingType } from 'store/selectors/selectors';
import useIsMobile from 'hooks/use-is-mobile';
import AmenitiesInOutSummary from 'common/spot-details/amenities-inout-summary';
import { getAirportPriceWODiscount } from 'utils/airport/price-utils';
import { AmenityTypes } from 'utils/types/amenity';
import AmenitiesInfo from 'common/amenities-info';
/*
    Styles specific to airport facility summary price without discount.
    Soon this will be applied across all verticals and discussion is underway to
    standardize the price without discount styles.
    Follow this PR https://github.com/spothero/consumer-web/pull/3425 and add styles and clean up classNames as needed.
    The classNames added in the Price component and DisplayPriceFormatted component are to set styles from parent components.
    If this is standardized these classNames can be removed and styles can be directly applied in the Price component.
*/
const AirportStyles = {
    '.FacilitySummary-PriceWithoutDiscountContainer': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        '.FacilitySummary-PriceWithoutDiscount': {
            width: 'max-content',
            pt: '1.5',
            fontSize: 'sm',
            fontWeight: 'normal',
            '.DisplayPriceFormatted-TextToAppend,.DisplayPriceFormatted-CurrencySymbol,.DisplayPriceFormatted-Price': {
                color: 'var(--chakra-colors-text-secondary-light)',
            },
        },
    },
};
const FacilitySummary = ({ isAdmin, showAdminLink, spot, priceLabel: propLabel, showFullTitle = false, showPrice = true, showImages = true, showFlair = true, appleMapsPartner, CTAs, isShowSpotsLeftVisible, imageWidth = 192, imageHeight = 192, staticBaseUrl, isPowerBooking, view, AirportDistance = null, AirportTransportationInfo = null, AirportTag = null, showInOut = false, }) => {
    const dispatch = useDispatch();
    const { allAmenities, spotId, title, distance, rating: { average: averageRating, count: numberOfReviews }, availability, visualFlags = [], images, hasMultipleRates, } = spot;
    const hasInOutAmenity = allAmenities?.some(amenity => amenity?.type === AmenityTypes.IN_OUT);
    const { available, unavailableReasons } = availability;
    const parkingType = useSelector(getParkingType);
    const isAirport = parkingType === 'airport';
    const priceObject = usePrice(spot);
    const priceLabel = useLabel({
        priceLabel: propLabel,
        selectedRate: spot?.selectedRate,
    });
    // Below two lines ensure oversized spots have total with a star.  Can't be in useLabel due to lack of spot awareness
    const { isOversize } = getSpotheroOversizeFee(spot);
    const labelOversizeIncluded = isOversize ? 'Total*' : priceLabel;
    const showTotal = useShowTotal();
    const oversizeFee = getSpotheroOversizeFee(spot);
    const classes = classNames('FacilitySummary');
    const titleClasses = classNames('FacilitySummary-title');
    const isMobile = useIsMobile();
    const imgProps = {
        width: imageWidth,
        height: imageHeight,
        alt: `Photo of ${title}`,
        lazyLoad: true,
        ...(images?.[0]
            ? { cloudinaryTemplate: images[0].urlTemplate }
            : { src: `${staticBaseUrl}img/missing-image.png` }),
    };
    const additionalStyles = classNames({
        'apply-fixed-width': isPowerBooking,
        Oversize: oversizeFee.isOversize,
        'apply-airport-styles': isAirport,
    });
    const priceWithoutDiscount = isAirport
        ? getAirportPriceWODiscount({ selectedRate: spot?.selectedRate })
        : breakdownWODiscountGenerator(spot?.selectedRate?.priceBreakdown, showTotal);
    const handleOnShowPriceBreakdown = () => {
        dispatch(setPriceBreakdownModalSpotId(spotId));
    };
    const priceCustomTextToAppend = isAirport ? '/day' : '';
    return (<Flex flexFlow="column" height="100%" data-testid={`FacilitySummary-${encodeURIComponent(title)}`} className="FacilitySummary-container">
            {AirportTag}
            {showFlair &&
            visualFlags.map((flag, i) => (<VisualFlag key={`visualfalg:${spotId}:${i}`} {...flag} view={view}/>))}
            <Flex justifyContent="space-between" position="relative" padding={isMobile ? '12px' : '16px'} height="100%" className={classes} data-testid="FacilitySummary-Images">
                {showImages && (<Box height={{ base: '80px', tablet: '96px' }} width={{ base: '80px', tablet: '96px' }} marginRight="16px" cursor="pointer" flexShrink="0" sx={{
                img: {
                    borderRadius: '8px',
                },
            }} data-testid="images-wrapper">
                        <Image {...imgProps}/>
                    </Box>)}
                <Flex flexGrow="1" minWidth="0" minHeight={isShowSpotsLeftVisible && { base: 32, tablet: 28 }} flexFlow="column">
                    <Flex>
                        <Box flexGrow="1" minWidth="0" color="gray.900">
                            <Heading as="h2" marginBottom="0">
                                <Text as="span" className={titleClasses} fontWeight="semibold" minHeight="auto" maxWidth={{
            base: '10rem',
            mobileXL: 'fit-content',
        }} lineHeight="1.2" display="block" fontSize="sm" overflow={showFullTitle ? 'visible' : 'hidden'} textOverflow={showFullTitle ? 'initial' : 'ellipsis'} whiteSpace={showFullTitle ? 'normal' : ''} padding={showPrice ? '0' : ''} data-testid="FacilitySummary-title">
                                    {title}
                                </Text>
                            </Heading>
                            <Box className="FacilitySummary-content">
                                {isAdmin && showAdminLink && (<Box fontSize="sm" display={{
                base: 'none',
                tablet: 'inline-block',
            }} marginLeft="8px">
                                        <Button variant="tertiary" fontSize="sm" as="a" href={`/admin/spots/${spotId}`}>
                                            Facility Admin
                                        </Button>
                                    </Box>)}
                                {AirportDistance}
                                <StarRatingWithCategory numberOfReviews={numberOfReviews} starRating={averageRating}/>
                                {showInOut && (<AmenitiesInOutSummary hasInOutAmenity={hasInOutAmenity} marginTop={1} iconStyles={{ color: 'black' }} spanStyles={{
                color: 'black',
            }}/>)}
                                {distance &&
            !appleMapsPartner &&
            !AirportDistance && (<WalkingDistanceDisplay linearMeters={distance.linearMeters} walkingMeters={distance.walkingMeters} walkingDuration={distance.durationSeconds}/>)}
                                {AirportTransportationInfo}
                                <AmenitiesInfo amenities={allAmenities}/>
                                {isAdmin && !available && (<Text as="span" fontSize="sm" display="block">
                                        <sup>*</sup>{' '}
                                        {unavailableReasons.join('; ')}
                                    </Text>)}
                            </Box>
                        </Box>
                        {showPrice && available && (<Box sx={isAirport ? AirportStyles : {}}>
                                {oversizeFee.isOversize ? (<Box minWidth={{
                    base: '88px',
                    tablet: '120px',
                }}>
                                        <Price oversizeFee={oversizeFee} priceWithoutDiscount={priceWithoutDiscount} price={priceObject} additionalStyles={additionalStyles} priceLabel={labelOversizeIncluded} hasMultipleRates={hasMultipleRates} onShowPriceBreakdown={handleOnShowPriceBreakdown} textToAppend={priceCustomTextToAppend}/>
                                    </Box>) : (<Price priceWithoutDiscount={priceWithoutDiscount} price={priceObject} additionalStyles={additionalStyles} priceLabel={priceLabel} hasMultipleRates={hasMultipleRates} onShowPriceBreakdown={handleOnShowPriceBreakdown} textToAppend={priceCustomTextToAppend}/>)}
                            </Box>)}
                    </Flex>
                    <Box flexGrow="1" minHeight={2}/>
                    <Box className="controls" data-testid="FacilitySummary-CTA">
                        {CTAs}
                    </Box>
                </Flex>
            </Flex>
        </Flex>);
};
export default withConfig(['staticBaseUrl'])(FacilitySummary);
